import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const CoachMark = ({
  targetRef, // { current: DOM element }
  isOpen,
  onClose,
  children,
  width = 200,
  offset = 12 // spacing from element
}) => {
  const tooltipRef = useRef(null)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [isBelow, setIsBelow] = useState(false)
  const [arrowOffset, setArrowOffset] = useState(width / 2)

  useEffect(() => {
    if (!isOpen || !targetRef?.current) return

    const calculatePosition = () => {
      const el = targetRef.current
      const rect = el.getBoundingClientRect()
      const scrollY = window.scrollY
      const scrollX = window.scrollX
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      const tooltipHeight = tooltipRef.current?.offsetHeight || 120

      // Vertical placement
      const spaceAbove = rect.top
      const spaceBelow = viewportHeight - rect.bottom
      let topPos, showBelow

      if (spaceAbove > tooltipHeight + offset) {
        topPos = rect.top + scrollY - tooltipHeight - offset
        showBelow = false
      } else if (spaceBelow > tooltipHeight + offset) {
        topPos = rect.bottom + scrollY + offset
        showBelow = true
      } else {
        if (spaceBelow > spaceAbove) {
          topPos = rect.bottom + scrollY + offset
          showBelow = true
        } else {
          topPos = Math.max(8, scrollY + 8)
          showBelow = false
        }
      }

      // Horizontal placement
      let leftPos = rect.left + scrollX + rect.width / 2 - width / 2
      if (leftPos < 8) leftPos = 8
      if (leftPos + width > viewportWidth - 20)
        leftPos = viewportWidth - width - 20

      // Arrow position inside tooltip
      let arrowLeft = rect.left + rect.width / 2 - leftPos
      arrowLeft = Math.max(8, Math.min(width - 8, arrowLeft))

      setPosition({ top: topPos, left: leftPos })
      setIsBelow(showBelow)
      setArrowOffset(arrowLeft)
    }

    // Initial position
    calculatePosition()

    // Recalculate on scroll, resize, and layout changes
    window.addEventListener('scroll', calculatePosition)
    window.addEventListener('resize', calculatePosition)

    const observer = new ResizeObserver(calculatePosition)
    observer.observe(targetRef.current)

    return () => {
      window.removeEventListener('scroll', calculatePosition)
      window.removeEventListener('resize', calculatePosition)
      observer.disconnect()
    }
  }, [isOpen, targetRef, width, offset])

  if (!isOpen || !targetRef?.current) return null

  return createPortal(
    <div
      ref={tooltipRef}
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        width: `${width}px`,
        background: '#000',
        color: '#fff',
        padding: '16px 16px 8px 16px',
        borderRadius: '4px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        zIndex: 99,
        transform: 'translateY(0)'
      }}
      role='dialog'
      aria-modal='true'
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'transparent',
          border: 'none',
          color: 'white',
          fontSize: '16px',
          cursor: 'pointer'
        }}
        aria-label='Close'
      >
        ✕
      </button>

      {/* Tooltip content */}
      <div style={{ marginTop: '12px' }}>{children}</div>

      {/* Arrow */}
      <div
        style={{
          position: 'absolute',
          left: `${arrowOffset}px`,
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderTop: isBelow ? 'none' : '8px solid #000',
          borderBottom: isBelow ? '8px solid #000' : 'none',
          top: isBelow ? '-8px' : 'auto',
          bottom: isBelow ? 'auto' : '-8px'
        }}
      />
    </div>,
    document.body
  )
}

export default CoachMark
